// app/javascript/controllers/photo_upload_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "fileInput", "previewContainer", "message", "fieldsContainer"]

  connect() {
    this.uploadedFiles = []
    this.nextIndex = this.calculateNextIndex()
    this.setupEventListeners()
  }

  disconnect() {
    this.removeEventListeners()
  }

  setupEventListeners() {
    if (this.hasDropzoneTarget && this.hasFileInputTarget) {
      // Click on dropzone
      this.dropzoneTarget.addEventListener('click', this.handleDropzoneClick.bind(this))
      
      // File input change
      this.fileInputTarget.addEventListener('change', this.handleFileChange.bind(this))
      
      // Drag events
      const dragEvents = ['dragenter', 'dragover', 'dragleave', 'drop']
      dragEvents.forEach(eventName => {
        this.dropzoneTarget.addEventListener(eventName, this.preventDefault.bind(this), false)
      })
      
      // Style changes on drag
      this.dropzoneTarget.addEventListener('dragenter', this.addDragoverClass.bind(this), false)
      this.dropzoneTarget.addEventListener('dragover', this.addDragoverClass.bind(this), false)
      this.dropzoneTarget.addEventListener('dragleave', this.removeDragoverClass.bind(this), false)
      this.dropzoneTarget.addEventListener('drop', this.handleDrop.bind(this), false)
    }
  }

  removeEventListeners() {
    if (this.hasDropzoneTarget && this.hasFileInputTarget) {
      this.dropzoneTarget.removeEventListener('click', this.handleDropzoneClick.bind(this))
      this.fileInputTarget.removeEventListener('change', this.handleFileChange.bind(this))
      
      // Remove all other event listeners
      // In a real implementation you might want to store references to the bound handlers
    }
  }

  preventDefault(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  addDragoverClass() {
    this.dropzoneTarget.classList.add('dragover')
  }

  removeDragoverClass() {
    this.dropzoneTarget.classList.remove('dragover')
  }

  handleDropzoneClick() {
    this.fileInputTarget.click()
  }

  handleFileChange(e) {
    this.processFiles(e.target.files)
    this.fileInputTarget.value = '' // Reset the input
  }

  handleDrop(e) {
    this.preventDefault(e)
    this.removeDragoverClass()
    this.processFiles(e.dataTransfer.files)
  }

  processFiles(files) {
    if (!files || files.length === 0) return
    
    // Show the preview container
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.style.display = 'flex'
      if (this.hasMessageTarget) {
        this.messageTarget.style.display = 'none'
      }
    }
    
    // Process each file
    Array.from(files).forEach(file => {
      if (!file.type.match('image.*')) return
      
      // Store the file with the next available index
      const fileIndex = this.nextIndex++
      this.uploadedFiles.push({ index: fileIndex, file: file })
      
      // Create preview
      this.createPreview(fileIndex, file)
    })
    
    // Update hidden fields
    this.updateHiddenFields()
  }

  createPreview(fileIndex, file) {
    const colDiv = document.createElement('div')
    colDiv.className = 'col-4 col-md-3 col-lg-4'
    colDiv.dataset.fileIndex = fileIndex
    
    const previewDiv = document.createElement('div')
    previewDiv.className = 'photo-preview'
    
    // Create image preview
    const img = document.createElement('img')
    const reader = new FileReader()
    reader.onload = (e) => {
      img.src = e.target.result
    }
    reader.readAsDataURL(file)
    
    // Create filename display
    const filenameDiv = document.createElement('div')
    filenameDiv.className = 'filename'
    filenameDiv.textContent = file.name
    
    // Create remove button
    const removeBtn = document.createElement('div')
    removeBtn.className = 'remove-photo'
    removeBtn.innerHTML = '<i class="bi bi-x"></i>'
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation()
      
      // Remove this file from the uploaded files array
      this.uploadedFiles = this.uploadedFiles.filter(item => item.index !== fileIndex)
      colDiv.remove()
      
      // Show message if no files and no existing photos
      if (this.previewContainerTarget.querySelectorAll('.col-4').length === 0 && 
          document.querySelectorAll('.product-photo-item').length === 0) {
        this.previewContainerTarget.style.display = 'none'
        if (this.hasMessageTarget) {
          this.messageTarget.style.display = 'block'
        }
      }
      
      // Update hidden fields
      this.updateHiddenFields()
    })
    
    // Assemble preview
    previewDiv.appendChild(img)
    previewDiv.appendChild(filenameDiv)
    previewDiv.appendChild(removeBtn)
    colDiv.appendChild(previewDiv)
    
    this.previewContainerTarget.appendChild(colDiv)
  }

  updateHiddenFields() {
    if (!this.hasFieldsContainerTarget) return
    
    // Clear container first
    this.fieldsContainerTarget.innerHTML = ''
    
    // Add fields for valid files
    this.uploadedFiles.forEach(item => {
      // Calculate position (existing items + index in new files)
      const position = document.querySelectorAll('.product-photo-item').length + 
                       this.calculatePositionInNewFiles(item.index)
      
      // Create hidden file field
      const hiddenField = document.createElement('input')
      hiddenField.type = 'file'
      hiddenField.name = `product[product_photos_attributes][${item.index}][image]`
      hiddenField.style.display = 'none'
      
      // Create a DataTransfer object and add the file
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(item.file)
      hiddenField.files = dataTransfer.files
      
      // Create a hidden position field
      const positionField = document.createElement('input')
      positionField.type = 'hidden'
      positionField.name = `product[product_photos_attributes][${item.index}][position]`
      positionField.value = position
      
      this.fieldsContainerTarget.appendChild(hiddenField)
      this.fieldsContainerTarget.appendChild(positionField)
    })
  }

  calculateNextIndex() {
    // Find the highest index from existing photo fields
    let nextIndex = 0
    const existingPhotoFields = document.querySelectorAll('[name^="product[product_photos_attributes]"][name$="[id]"]')
    
    existingPhotoFields.forEach(field => {
      const match = field.name.match(/\[(\d+)\]/)
      if (match && match[1]) {
        const index = parseInt(match[1])
        nextIndex = Math.max(nextIndex, index + 1)
      }
    })
    
    return nextIndex
  }

  calculatePositionInNewFiles(fileIndex) {
    // Find position of this file among new files
    const files = this.uploadedFiles.sort((a, b) => a.index - b.index)
    return files.findIndex(file => file.index === fileIndex)
  }
}